import { useEffect, useState } from "react";
import { api } from "../api/client";

export default function ConfigPage() {
  const [cfg, setCfg] = useState<any>(null);
  const [msg, setMsg] = useState("");
  const [falla, setFalla] = useState<string | null>(null);
  // Para poder decir la consecuencia concreta de cada ajuste en vez de
  // describirlo en abstracto: "esconderían 423" pesa distinto que
  // "solo se valida lo confirmado".
  const [cola, setCola] = useState<any>(null);

  useEffect(() => {
    api.getMetrics().then(setCola).catch(() => setCola(null));
  }, []);

  function cargar() {
    setFalla(null);
    api.getConfig(true).then(setCfg)
      // Sin esto la pantalla se quedaba en "Cargando configuracion..." para
      // siempre: una falla del API se veia igual que estar cargando.
      .catch((e) => setFalla(e?.message || "No se pudo cargar la configuracion."));
  }

  useEffect(cargar, []);

  async function guardar(updates: Record<string, unknown>) {
    setMsg("Guardando...");
    try {
      await api.setConfig(updates);
      setCfg(await api.getConfig());
      setMsg("Guardado");
    } catch (e: any) {
      setMsg("Error: " + e.message);
    }
  }

  async function pasarALive() {
    if (!confirm("Cambiar a modo LIVE: las decisiones van a modificar el Admin de verdad. ¿Seguro?")) return;
    if (!confirm("Confirmación final: activar LIVE en producción.")) return;
    await guardar({ EXECUTION_MODE: "live", confirm: "YES-LIVE" });
  }

  if (falla)
    return (
      <div className="empty falla">
        <div className="falla-titulo">No se pudo cargar la configuración.</div>
        <div className="falla-detalle mono">{falla}</div>
        <button className="btn" onClick={cargar}>Reintentar</button>
      </div>
    );

  if (!cfg) return <div className="empty">Cargando configuración...</div>;

  const simulando = cfg.EXECUTION_MODE !== "live";
  // Sin valor guardado se asume prendido: es como venia funcionando, y un
  // default silencioso no puede dejar al equipo sin avisos.
  const chatPrendido = cfg.NOTIFICAR_CHAT !== false;
  const exigeArca = cfg.EXIGIR_ARCA !== false;
  const loteActivo = cfg.APROBACION_LOTE === true;
  // Mismo criterio que el backend: la config de PROD guarda solo lo que
  // alguien cambió, así que una clave ausente significa "procesando".
  const procesando = cfg.PROCESAR_DEPOSITOS !== false;
  // El endpoint lo calcula sobre la cola abierta, que es la cuenta exacta.
  // Las métricas quedan de respaldo para cuando no vino.
  const sinArca = cfg.efectos?.exigir_arca_ocultaria ?? (
    cola?.arca_estados
      ? cola.arca_estados
          .filter(([e]: [string, number]) => e !== "CONFIRMADO")
          .reduce((a: number, [, n]: [string, number]) => a + n, 0)
      : 0);

  return (
    <div style={{ display: "grid", gap: 14, maxWidth: 720 }}>
      {msg && (
        <div className="card card-pad" style={{ fontSize: 12.5, color: "var(--muted)" }}>
          {msg}
        </div>
      )}

      {/* El modo de ejecución va aislado y con borde rojo: es el único
          ajuste de esta pantalla que mueve plata de verdad. */}
      <div className="cfg-riesgo">
      <Fila
        titulo="Modo de ejecución"
        valor={simulando ? "Simulación (CERT)" : "Real (LIVE)"}
        tono={simulando ? "warn" : "danger"}
        detalle={simulando
          ? "Las aprobaciones y rechazos se registran pero NO tocan el Admin. Es el modo seguro para observar cómo decide el agente."
          : "Las decisiones modifican el Admin de verdad."}
        accion={simulando
          ? <button className="btn danger" onClick={pasarALive}>Activar modo real</button>
          : <button className="btn" onClick={() => guardar({ EXECUTION_MODE: "cert" })}>
              Volver a simulación
            </button>}
      />

      <Auditoria cfg={cfg} clave="EXECUTION_MODE" verbo="Activado por" />
      </div>

      {/* Pausa del pipeline. Va arriba del resto porque es el único ajuste
          que detiene el trabajo por completo: si está apagado, ninguno de los
          de abajo hace nada. */}
      <Fila
        titulo="Procesamiento de depósitos"
        valor={procesando ? "Activo" : "En pausa"}
        tono={procesando ? undefined : "danger"}
        detalle={procesando
          ? "El agente consulta Admin cada 3 minutos y analiza cada voucher nuevo."
          : "No se consulta Admin ni se analiza ningún voucher: el agente no gasta nada mientras esté así. Los depósitos se siguen acumulando en Admin y entran solos a los 3 minutos de reanudar — no se pierde ninguno."}
        accion={
          <Switch prendido={procesando}
                  etiqueta="Procesar depósitos nuevos"
                  onCambio={(v) => guardar({ PROCESAR_DEPOSITOS: v })} />}
      />
      {!procesando && <Auditoria cfg={cfg} clave="PROCESAR_DEPOSITOS" verbo="Pausado por" soloPorClave />}

      <Fila
        titulo="Quién decide"
        valor={cfg.MODE === "auto" ? "El agente aprueba solo" : "Siempre decide una persona"}
        tono={cfg.MODE === "auto" ? "warn" : undefined}
        detalle={cfg.MODE === "auto"
          ? `El agente aprueba sin intervención cuando todos los datos coinciden y su certeza supera ${Math.round((cfg.THRESHOLD ?? 0) * 100)}%. El resto sigue llegando a la cola.`
          : "Todos los depósitos pasan por la cola de validación, sin importar la certeza."}
        accion={
          <Switch prendido={cfg.MODE === "auto"}
                  etiqueta="El agente puede aprobar solo"
                  onCambio={(v) => guardar({ MODE: v ? "auto" : "manual" })} />}
      />

      <Fila
        titulo="Certeza mínima para aprobar solo"
        valor={`${Math.round((cfg.THRESHOLD ?? 0) * 100)}%`}
        detalle="Debajo de este nivel el depósito siempre llega a la cola, aunque el agente esté en modo automático."
        accion={
          <span className="rev-seg">
            {[0.8, 0.85, 0.9, 0.95, 0.98].map((v) => (
              <button key={v} className={cfg.THRESHOLD === v ? "on" : ""}
                      onClick={() => guardar({ THRESHOLD: v })}>
                {Math.round(v * 100)}%
              </button>
            ))}
          </span>}
      />

      <Fila
        titulo="Exigir confirmación de ARCA"
        valor={exigeArca ? "Solo se valida lo confirmado" : "Se valida todo"}
        tono={exigeArca ? undefined : "warn"}
        detalle={exigeArca
          ? `La cola solo muestra depósitos cuyo abono ARCA ya confirmó.${sinArca ? ` Hoy eso deja ${sinArca} esperando aparte.` : ""} Si ARCA no carga movimientos, la cola puede quedar vacía.`
          : `Todos entran a la cola, tengan o no abono confirmado.${sinArca ? ` Hoy son ${sinArca} con el voucher válido pero sin confirmar que la plata entró.` : ""}`}
        accion={
          <Switch prendido={exigeArca}
                  etiqueta="Exigir abono confirmado"
                  onCambio={(v) => guardar({ EXIGIR_ARCA: v })} />}
      />

      <Fila
        titulo="Aprobar varios de una"
        valor={loteActivo ? "Habilitado" : "Se aprueba de a uno"}
        tono={loteActivo ? "warn" : undefined}
        detalle={loteActivo
          ? "El analista puede seleccionar depósitos y aprobarlos juntos, con un modal de confirmación obligatorio. Confirma sobre una lista de nombres y montos, sin abrir cada comprobante."
          : "Cada depósito se aprueba con su voucher a la vista. Es más lento y es la razón por la que nadie aprueba algo que no miró."}
        accion={
          <Switch prendido={loteActivo}
                  etiqueta="Permitir aprobar en lote"
                  onCambio={(v) => guardar({ APROBACION_LOTE: v })} />}
      />

      <Fila
        titulo="Aviso en Google Chat"
        valor={chatPrendido ? "Se publica una tarjeta por depósito" : "Solo el portal"}
        tono={chatPrendido ? undefined : "warn"}
        detalle={chatPrendido
          ? "Cada depósito llega también a Google Chat. Apagalo cuando el equipo trabaje solo desde el portal."
          : "No se publican tarjetas nuevas. Las que ya están publicadas se siguen actualizando al decidir, para que ninguna quede abierta mostrando como pendiente algo ya resuelto."}
        accion={
          <Switch prendido={chatPrendido}
                  etiqueta="Publicar tarjeta en Chat"
                  onCambio={(v) => guardar({ NOTIFICAR_CHAT: v })} />}
      />
    </div>
  );
}

/** Quién tocó este ajuste y cuándo.
 *
 *  Por clave si está; si no, el último cambio global. Los registros anteriores
 *  a que se guardara la auditoría por clave solo tienen eso, y el global solo
 *  dice quién tocó la configuración por última vez —no quién encendió esto.
 *
 *  `soloPorClave` renuncia a ese respaldo. Sirve para los ajustes que no
 *  existían antes de la auditoría por clave: ahí el dato global no es un
 *  respaldo sino una línea sobre otra cosa, debajo de un título que la hace
 *  leer como si respondiera «quién lo pausó». */
function Auditoria({ cfg, clave, verbo, soloPorClave }: {
  cfg: any; clave: string; verbo: string; soloPorClave?: boolean;
}) {
  const a = cfg.auditoria?.[clave];
  if (soloPorClave && !a) return null;
  const por = a?.por ?? cfg.updated_by;
  const ts = a?.ts ?? cfg.updated_at;
  if (!por) return null;
  return (
    <div className="cfg-auditoria mono">
      {a ? verbo : "Último cambio de configuración:"} {por}
      {ts && <> · {new Date(ts * 1000).toLocaleString("es-PE",
        { day: "2-digit", month: "short", year: "numeric",
          hour: "2-digit", minute: "2-digit" })}</>}
    </div>
  );
}

/** Interruptor. Se usa para lo que es prendido o apagado; un botón que dice
 *  «Activar» obliga a leer el texto para saber en qué estado está. */
function Switch({ prendido, etiqueta, onCambio }: {
  prendido: boolean; etiqueta: string; onCambio: (v: boolean) => void;
}) {
  return (
    <button className={"switch" + (prendido ? " on" : "")}
            role="switch" aria-checked={prendido} aria-label={etiqueta}
            onClick={() => onCambio(!prendido)}>
      <span className="switch-pulgar" />
    </button>
  );
}

function Fila({ titulo, valor, detalle, accion, tono }: {
  titulo: string; valor: string; detalle: string;
  accion: React.ReactNode; tono?: "warn" | "danger";
}) {
  return (
    <div className="card card-pad">
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11.5, textTransform: "uppercase",
                        letterSpacing: ".06em", color: "var(--muted)" }}>
            {titulo}
          </div>
          <div style={{ fontSize: 17, fontWeight: 600, marginTop: 4,
                        color: tono ? `var(--${tono})` : undefined }}>
            {valor}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 6, lineHeight: 1.5 }}>
            {detalle}
          </div>
        </div>
        <div style={{ flexShrink: 0 }}>{accion}</div>
      </div>
    </div>
  );
}
